import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowSquareOut, Microphone, PaperPlaneTilt, SpinnerGap, X } from '@phosphor-icons/react';
import { AthenaLogo } from './AthenaLogo';
import { ATHENA_INTENT_TYPES } from '../platform/AthenaIntentRouter';

const QUICK_INTENTS = new Set([
  ATHENA_INTENT_TYPES.OPEN_QUICK,
  ATHENA_INTENT_TYPES.START_VOICE,
  ATHENA_INTENT_TYPES.CAPTURE_IDEA,
  ATHENA_INTENT_TYPES.NEW_TASK,
  ATHENA_INTENT_TYPES.NEW_REMINDER,
  ATHENA_INTENT_TYPES.IMPORT_CONTENT,
]);

const COPY = Object.freeze({
  [ATHENA_INTENT_TYPES.OPEN_QUICK]: {
    title: 'Converse com a Athena',
    description: 'Escreva uma mensagem ou toque no microfone para usar a voz manual.',
    placeholder: 'Como a Athena pode ajudar?',
    submitLabel: 'Enviar mensagem',
  },
  [ATHENA_INTENT_TYPES.START_VOICE]: {
    title: 'Fale com a Athena',
    description: 'A gravação só começa depois que você tocar no microfone.',
    placeholder: 'Ou escreva sua mensagem…',
    submitLabel: 'Enviar mensagem',
  },
  [ATHENA_INTENT_TYPES.CAPTURE_IDEA]: {
    title: 'Registrar uma ideia',
    description: 'Escreva ou dite a ideia. Ela entra na mesma conversa da Athena.',
    placeholder: 'Qual ideia você quer guardar?',
    submitLabel: 'Registrar ideia',
  },
  [ATHENA_INTENT_TYPES.NEW_TASK]: {
    title: 'Nova tarefa',
    description: 'Diga o que precisa ser feito e, se quiser, informe prazo e prioridade.',
    placeholder: 'Ex.: enviar orçamento amanhã às 10h',
    submitLabel: 'Criar tarefa',
  },
  [ATHENA_INTENT_TYPES.NEW_REMINDER]: {
    title: 'Novo lembrete',
    description: 'Inclua o horário. A Athena confirma somente depois de salvar o lembrete.',
    placeholder: 'Ex.: me lembrar de ligar para a Ana amanhã às 9h',
    submitLabel: 'Criar lembrete',
  },
  [ATHENA_INTENT_TYPES.IMPORT_CONTENT]: {
    title: 'Salvar conteúdo',
    description: 'Envie um texto curto para a Athena guardar na sua biblioteca.',
    placeholder: 'Cole ou escreva o conteúdo…',
    submitLabel: 'Salvar conteúdo',
  },
});

function cleanShortResponse(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[`*_>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320);
}

function sourceLabel(source) {
  if (source === 'widget') return 'Widget';
  if (source === 'tile') return 'Atalho rápido';
  if (source === 'shortcut') return 'Atalho do app';
  if (source === 'deep_link') return 'Atalho';
  return 'Athena';
}

function stateFor({ voiceState, sessionRestoring, submitting }) {
  if (sessionRestoring) return { label: 'Restaurando conversa', color: '#a5b4fc' };
  if (submitting || voiceState === 'processing') return { label: 'Pensando', color: '#c4b5fd' };
  if (voiceState === 'active') return { label: 'Ouvindo', color: '#5eead4' };
  if (voiceState === 'speaking') return { label: 'Respondendo', color: '#93c5fd' };
  return { label: 'Pronta', color: '#86efac' };
}

/**
 * Compact entry surface shared by the Android tile, widget and shortcuts.
 * It deliberately delegates every send to the Home conversation handler: it
 * has no separate session, model request, alarm or action implementation.
 */
export function QuickAthenaPanel({
  intent,
  lastResponse = '',
  voiceState = 'idle',
  voiceLevel = 0,
  sessionRestoring = false,
  onSubmit,
  onVoiceToggle,
  onDismiss,
  onExpand,
}) {
  const copy = COPY[intent?.type];
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const response = useMemo(() => cleanShortResponse(lastResponse), [lastResponse]);
  const state = stateFor({ voiceState, sessionRestoring, submitting });
  const isListening = voiceState === 'active';
  const isBusy = sessionRestoring || submitting || voiceState === 'processing';
  const level = Math.max(0, Math.min(1, Number(voiceLevel) || 0));

  useEffect(() => {
    setText('');
    setSubmitting(false);
  }, [intent?.id]);

  if (!intent || !copy || !QUICK_INTENTS.has(intent.type)) return null;

  const submit = async (event) => {
    event?.preventDefault?.();
    const content = text.trim();
    if (!content || isBusy) return;
    setSubmitting(true);
    try {
      const outcome = await onSubmit?.({ intent, text: content });
      // A Home lock/de-dupe refusal happens before any message reaches the
      // conversation. Keep the draft in this compact surface so the person
      // can send it as soon as Athena is ready.
      if (outcome?.accepted !== false) setText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      aria-label="Athena rápida"
      aria-modal="true"
      role="dialog"
      style={{
        position: 'fixed',
        zIndex: 10030,
        left: 14,
        right: 14,
        bottom: 'calc(14px + var(--athena-home-keyboard-offset, 0px) + env(safe-area-inset-bottom, 0px))',
        maxWidth: 560,
        margin: '0 auto',
        padding: '14px 14px 13px',
        color: '#eaf3ff',
        border: '1px solid rgba(96, 165, 250, 0.34)',
        borderRadius: 22,
        background: 'linear-gradient(135deg, rgba(7, 21, 44, 0.98), rgba(5, 11, 29, 0.98))',
        boxShadow: '0 18px 62px rgba(0, 0, 0, 0.48), 0 0 48px rgba(37, 99, 235, 0.12)',
        backdropFilter: 'blur(18px)',
      }}
      data-testid="quick-athena-panel"
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <AthenaLogo size={30} showText={false} style={{ filter: 'drop-shadow(0 0 9px rgba(96,165,250,0.45))' }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <strong style={{ fontSize: 14, letterSpacing: '0.01em' }}>Athena rápida</strong>
            <span style={{ color: 'rgba(191, 219, 254, 0.55)', fontSize: 11 }}>• {sourceLabel(intent.source)}</span>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 3, color: state.color, fontSize: 11, fontWeight: 650 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: state.color, boxShadow: `0 0 10px ${state.color}` }} />
            {state.label}
          </span>
        </div>
        <button
          type="button"
          aria-label="Fechar Athena rápida"
          onClick={() => onDismiss?.(intent)}
          style={{ display: 'grid', placeItems: 'center', width: 32, height: 32, padding: 0, color: 'rgba(219,234,254,0.75)', border: 0, background: 'transparent', cursor: 'pointer' }}
        >
          <X size={19} />
        </button>
      </header>

      <div style={{ marginTop: 13, padding: '11px 12px', borderRadius: 14, border: '1px solid rgba(148, 163, 184, 0.14)', background: 'rgba(2, 8, 23, 0.5)' }}>
        <strong style={{ display: 'block', color: '#dbeafe', fontSize: 14 }}>{copy.title}</strong>
        <span style={{ display: 'block', marginTop: 3, color: 'rgba(219, 234, 254, 0.66)', fontSize: 12, lineHeight: 1.45 }}>{copy.description}</span>
      </div>

      {response ? (
        <div aria-live="polite" style={{ marginTop: 10, padding: '9px 11px', borderLeft: '2px solid rgba(96,165,250,0.68)', color: 'rgba(219,234,254,0.78)', fontSize: 12, lineHeight: 1.48 }}>
          <span style={{ display: 'block', marginBottom: 2, color: '#93c5fd', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Última resposta</span>
          {response}
        </div>
      ) : null}

      <form onSubmit={submit} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 11 }}>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(event) => setText(event.target.value.slice(0, 2000))}
          placeholder={copy.placeholder}
          rows={isListening ? 1 : 2}
          maxLength={2000}
          disabled={isBusy}
          aria-label={copy.placeholder}
          style={{
            boxSizing: 'border-box',
            flex: 1,
            minWidth: 0,
            minHeight: isListening ? 42 : 58,
            maxHeight: 110,
            padding: '10px 11px',
            resize: 'none',
            color: '#f8fbff',
            font: 'inherit',
            fontSize: 14,
            lineHeight: 1.35,
            borderRadius: 13,
            border: `1px solid ${isListening ? 'rgba(45, 212, 191, 0.54)' : 'rgba(148, 163, 184, 0.25)'}`,
            outline: 'none',
            background: isListening ? 'rgba(6, 78, 59, 0.16)' : 'rgba(2, 8, 23, 0.76)',
          }}
        />
        <button
          type="button"
          onClick={() => {
            // A focused textarea can leave the Android keyboard over the
            // capture state. The microphone still starts only on this click.
            inputRef.current?.blur?.();
            onVoiceToggle?.();
          }}
          disabled={isBusy}
          aria-label={isListening ? 'Parar e enviar áudio' : 'Iniciar gravação manual'}
          title={isListening ? 'Parar e enviar áudio' : 'Iniciar gravação manual'}
          style={{
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            width: 44,
            height: 44,
            padding: 0,
            color: isListening ? '#d9fffa' : '#dbeafe',
            borderRadius: 13,
            border: `1px solid ${isListening ? 'rgba(45, 212, 191, 0.68)' : 'rgba(96, 165, 250, 0.45)'}`,
            background: isListening ? 'rgba(13, 148, 136, 0.32)' : 'rgba(30, 64, 175, 0.24)',
            boxShadow: isListening ? `0 0 ${8 + level * 16}px rgba(45, 212, 191, ${0.24 + level * 0.3})` : 'none',
            cursor: isBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {isListening ? <PaperPlaneTilt size={19} weight="fill" /> : <Microphone size={20} />}
        </button>
        <button
          type="submit"
          disabled={!text.trim() || isBusy}
          aria-label={copy.submitLabel}
          title={copy.submitLabel}
          style={{
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            width: 44,
            height: 44,
            padding: 0,
            color: text.trim() && !isBusy ? '#ecfeff' : 'rgba(203,213,225,0.48)',
            borderRadius: 13,
            border: '1px solid rgba(34, 211, 238, 0.36)',
            background: text.trim() && !isBusy ? 'rgba(8, 145, 178, 0.3)' : 'rgba(71,85,105,0.18)',
            cursor: text.trim() && !isBusy ? 'pointer' : 'not-allowed',
          }}
        >
          {isBusy ? <SpinnerGap size={19} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
        </button>
      </form>

      <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
        <span style={{ color: 'rgba(191,219,254,0.48)', fontSize: 10 }}>Voz manual e texto usam a mesma conversa.</span>
        <button
          type="button"
          onClick={() => onExpand?.(intent)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0, color: '#93c5fd', fontSize: 12, fontWeight: 650, border: 0, background: 'transparent', cursor: 'pointer' }}
        >
          Expandir <ArrowSquareOut size={15} />
        </button>
      </footer>
    </section>
  );
}

export function isQuickAthenaIntent(intent) {
  return Boolean(intent?.type && QUICK_INTENTS.has(intent.type));
}

export default QuickAthenaPanel;
